/**
 * @file canvas-messages.ts
 * @description Сообщения кабинета и витрины в iframe предпросмотра
 * (phase19.16 Р1-6а; протокол витрины — apps/web lib/site/canvas.ts и
 * components/site/builder/preview-bridge.ts).
 *
 * Кабинет → витрина (маркер `source: "commora-admin"`):
 *  - `commora:site-theme-preview { theme, shopFonts }` — живое оформление:
 *    документ целиком, CSS витрина собирает сама тем же кодом shared;
 *    `shopFonts` — записи реестра своих шрифтов магазина (`ShopFontEntry`),
 *    на которые ссылается документ (Р1-7, SB99): витрина строит по ним
 *    `@font-face`, даже если шрифт загружен после открытия канвы. Поле
 *    кладётся, только когда список своих шрифтов получен: без него витрина
 *    берёт свои шрифты страницы (иначе до загрузки списка, без права
 *    настроек или при ошибке списка свой шрифт мигал бы системным);
 *  - `scroll-to { area }` — прокрутить к шапке или подвалу;
 *  - `block-highlight { blockId }` — подсветить выбранный блок (`null` — снять).
 * Витрина → кабинет (маркер `source: "chaiberi-preview"`):
 *  - `area-select { area }` — клик по шапке или подвалу на канве.
 * Корзина (SB120 этап 4) — `canvas-cart-messages.ts`.
 */

import {
  SECTION_PAYLOAD_VERSION,
  isShopFontEntry,
  sectionTypoFonts,
  themeShopFontKeys,
  type SectionPayload,
  type ShopFontEntry,
  type SiteFontRegistry,
  type SiteTheme,
} from "@chaiberi/shared";

/** Общая область сайта на канве. */
export type CanvasArea = "header" | "footer";

/** Маркер сообщений кабинета. */
export const CABINET_MESSAGE_SOURCE = "commora-admin";
/** Маркер сообщений канвы (витрины). */
export const CANVAS_MESSAGE_SOURCE = "chaiberi-preview";
/** Тип сообщения с живым оформлением. */
export const PREVIEW_THEME_MESSAGE = "commora:site-theme-preview";

/** Свои шрифты, на которые ссылается оформление (в порядке ролей); неизвестные ключи пропускаются. */
export function themeShopFonts(theme: SiteTheme, registry: SiteFontRegistry): ShopFontEntry[] {
  return themeShopFontKeys(theme)
    .map((key) => registry.find(key))
    .filter((entry): entry is ShopFontEntry => entry !== undefined && isShopFontEntry(entry));
}

/**
 * Сообщение с оформлением. `registry` — реестр с полученным списком своих
 * шрифтов; `null`/нет — список не получен, поле `shopFonts` не шлём.
 */
export function themeMessage(theme: SiteTheme, registry: SiteFontRegistry | null = null): ThemeMessage {
  const base = { source: CABINET_MESSAGE_SOURCE, type: PREVIEW_THEME_MESSAGE, theme } as const;
  return registry ? { ...base, shopFonts: themeShopFonts(theme, registry) } : base;
}

/** Сообщение с оформлением; `shopFonts` — только при полученном списке. */
export interface ThemeMessage {
  readonly source: typeof CABINET_MESSAGE_SOURCE;
  readonly type: typeof PREVIEW_THEME_MESSAGE;
  readonly theme: SiteTheme;
  readonly shopFonts?: ShopFontEntry[];
}

export function scrollToMessage(area: CanvasArea) {
  return { source: CABINET_MESSAGE_SOURCE, type: "scroll-to", area } as const;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** `area-select { area }` от канвы; `null` — не то сообщение. */
export function areaFromCanvasMessage(data: unknown): CanvasArea | null {
  if (!isObject(data) || data.source !== CANVAS_MESSAGE_SOURCE || data.type !== "area-select") return null;
  return data.area === "header" || data.area === "footer" ? data.area : null;
}

/**
 * `block-highlight { blockId }` — подсветить блок рамкой и прокрутить к нему
 * (выбор в структуре конструктора); `null` снимает подсветку.
 */
export function blockHighlightMessage(blockId: string | null) {
  return { source: CABINET_MESSAGE_SOURCE, type: "block-highlight", blockId } as const;
}

// ── phase19.16 Р2: секции v2 (контракт с apps/web canvas.ts / preview-bridge.ts) ──
//
// Кабинет → витрина:
//  - `block-draft { blockId, payloadVersion, payload | null, appearance, shopFonts? }` —
//    несохранённая форма секции: канва рисует блок из присланного payload
//    сразу, без записи на сайт; `payload: null` — вернуть сохранённое;
//    `shopFonts` (SB120 этап 3а) — записи реестра своих шрифтов, которые
//    стоят в `typo.*.font` черновика: канва пересобирает по ним `<style>`
//    шрифтов элементов; поле кладётся, только когда список своих шрифтов
//    получен (иначе свой шрифт мигал бы шрифтом роли);
//  - `space-highlight { blockId, part | null }` — подсветить на канве поле
//    отступа, рисунки или контур подложки (`card`), пока курсор над
//    настройкой (uiux-design-r2 §2, §7);
//  - `element-highlight { blockId, elementId | null }` — выбранный в панели
//    элемент.
// Витрина → кабинет (разбирает `usePreviewBridge`):
//  - `section-edit { blockId, path, value }` — текст правлен на канве;
//  - `element-select { blockId, path }` — клик по элементу;
//  - `block-duplicate { blockId }`, `block-add { afterBlockId | null }` —
//    «⧉» на ярлыке и «+ блок сюда» между блоками.

/**
 * Что подсветить в секции, пока курсор над настройкой. SB120 этап 1:
 * `inner-x` — внутренние отступы слева и справа (uiux-sb120-stage1 §2.2).
 * `card` — пунктирный контур подложки, пока курсор над флажком «На подложке
 * карточки сайта», скруглением или тенью подложки (решение 2026-10-19:
 * «меняю тень и скругление подложки — ничего не меняется»).
 */
export type SpaceHighlightPart = "outerTop" | "outerBottom" | "inner" | "inner-x" | "overlap" | "decor" | "card";

/** Свои шрифты элементов секции (`typo.*.font` с `shop:`), без повторов; неизвестные ключи пропускаются. */
export function sectionShopFonts(payload: SectionPayload, registry: SiteFontRegistry): ShopFontEntry[] {
  const seen = new Set<string>();
  const out: ShopFontEntry[] = [];
  for (const { key } of sectionTypoFonts(payload)) {
    if (seen.has(key)) continue;
    seen.add(key);
    const entry = registry.find(key);
    if (entry && isShopFontEntry(entry)) out.push(entry);
  }
  return out;
}

/**
 * Форма секции на канву; версия — текущая версия формата. `shopFonts` —
 * свои шрифты элементов черновика (`sectionShopFonts`), только при
 * полученном списке своих шрифтов; `undefined` — поле не шлём.
 */
export function blockDraftMessage(blockId: string, payload: unknown, appearance: unknown, shopFonts?: ShopFontEntry[]) {
  const base = { source: CABINET_MESSAGE_SOURCE, type: "block-draft", blockId, payloadVersion: SECTION_PAYLOAD_VERSION, payload, appearance } as const;
  return shopFonts ? { ...base, shopFonts } : base;
}

// ── SB120 этап 1: живой предпросмотр (TD-403; uiux-sb120-stage1 §4, db-design-sb120-stage1 §5) ──
//
// Кабинет → витрина: `commora:canvas-preview { blockId, seq, path, value }` —
// значение ползунка не чаще кадра; канва накладывает его на показанную
// секцию (`applySectionPreview` из shared) без формы и шага отмены и
// снимает, когда приходит `block-draft`. `value: null` — откат, `undefined`
// — «как у сайта» (снять ключ).
// Витрина → кабинет: `canvas-preview-ack { blockId, seq, ok }`.

/** Тип сообщения живого предпросмотра. */
export const CANVAS_PREVIEW_MESSAGE = "commora:canvas-preview";
/** Ответ канвы на предпросмотр. */
export const CANVAS_PREVIEW_ACK = "canvas-preview-ack";

export function canvasPreviewMessage(blockId: string, seq: number, path: string, value: unknown) {
  return { source: CABINET_MESSAGE_SOURCE, type: CANVAS_PREVIEW_MESSAGE, blockId, seq, path, value } as const;
}

/** Ответ канвы на предпросмотр. */
export interface CanvasPreviewAck {
  blockId: string;
  seq: number;
  ok: boolean;
}

/** `canvas-preview-ack` от канвы; `null` — не то сообщение. */
export function canvasPreviewAckOf(data: unknown): CanvasPreviewAck | null {
  if (!isObject(data) || data.source !== CANVAS_MESSAGE_SOURCE || data.type !== CANVAS_PREVIEW_ACK) return null;
  if (typeof data.blockId !== "string" || typeof data.seq !== "number") return null;
  return { blockId: data.blockId, seq: data.seq, ok: data.ok === true };
}

export function spaceHighlightMessage(blockId: string, part: SpaceHighlightPart | null) {
  return { source: CABINET_MESSAGE_SOURCE, type: "space-highlight", blockId, part } as const;
}

export function elementHighlightMessage(blockId: string, elementId: string | null) {
  return { source: CABINET_MESSAGE_SOURCE, type: "element-highlight", blockId, elementId } as const;
}

// ── phase19.16 Р2а: вид каталога (часть CATALOG) ──
//
// Кабинет → витрина: `commora:catalog-options-preview { options }` — вид
// каталога и карточки целиком (SiteCatalogOptions из shared) сразу после
// правки, до сохранения. Витрина, которая сообщение не знает, его
// пропускает: тогда новый вид появляется после сохранения части
// (перерисовка канвы по версии CATALOG, `canvasReloadKey`).

/** Тип сообщения с живым видом каталога. */
export const CATALOG_OPTIONS_MESSAGE = "commora:catalog-options-preview";

export function catalogOptionsMessage(options: unknown) {
  return { source: CABINET_MESSAGE_SOURCE, type: CATALOG_OPTIONS_MESSAGE, options } as const;
}

/** Сообщение кабинета в iframe канвы — только на origin витрины. */
export function postToCanvas(frame: HTMLIFrameElement | null, message: object): void {
  if (!frame?.contentWindow || !frame.src) return;
  try {
    frame.contentWindow.postMessage(message, new URL(frame.src).origin);
  } catch {
    // Адрес ещё не задан — повторим после загрузки.
  }
}
